<template>

  <v-container fluid class="pa-0">

    <div v-if="isReaderTypeSelectorVisible" class="app-sign-form-row">
      <v-radio-group v-model="readerType" class="ma-0 pa-0" row :disabled="componentDisabled" @change="onAuthTypeChanged">
        <v-radio v-if="isFileSystemReaderEnabled" :label="$t(`appSignCloudProviderCloudKey.fileSystemReader`)"
          :value="CloudKeyReaderType.FileSystem" color="primary">
        </v-radio>
        <v-radio v-if="isQrCodeReaderEnabled" :label="$t(`appSignCloudProviderCloudKey.qrCodeReader`)" :value="CloudKeyReaderType.QrCode"
          color="primary">
        </v-radio>
      </v-radio-group>
    </div>

    <app-sign-cloud-key-private-key-file-system-reader v-if="isFileSystemReaderRendered" v-show="isFileSystemReaderVisible"
      :certificateAuthority="certificateAuthority" :disabled="disabled" :eventBus="eventBus" :privateKeyType="privateKeyType"
      :signMethod="signMethod" :signProvider="cloudProvider" :useUserCertificates="useUserCertificates"
      :should-validate-key-type="shouldValidateKeyType" @keyReaded="onKeyReaded" @ready="onReady" @not-ready="onNotReady">
    </app-sign-cloud-key-private-key-file-system-reader>

    <app-sign-cloud-provider-default v-if="isQrCodeReaderRendered" v-show="isQrCodeReaderVisible"
      :certificateAuthority="certificateAuthority" :disabled="disabled" :eventBus="eventBus" :privateKeyType="privateKeyType"
      :signMethod="signMethod" :signProvider="cloudProvider" :useUserCertificates="useUserCertificates"
      :should-validate-key-type="shouldValidateKeyType" :inc-key-reader-type="CloudKeyReaderType.QrCode"
      @keyReaded="onKeyReaded" @ready="onReady" @not-ready="onNotReady" />

  </v-container>

</template>

<script>
import AppSignCloudKeyPrivateKeyFileSystemReader from './AppSignCloudKeyPrivateKeyFileSystemReader';
import AppSignCloudProviderDefault from '@/sign/components/AppSignCloudProviderDefault.vue';
import { actions as _act } from '@/models/store.actions';
import { CloudProvider, CloudKeyReaderType, SignMethod, PrivateKeyType } from '@/sign/models/sign-constants';
import { appSettingsHelper } from '@/helpers/settings-helper';

export default {
  name: 'AppSignCloudProviderCloudKey',
  components: {
    AppSignCloudKeyPrivateKeyFileSystemReader,
    AppSignCloudProviderDefault
  },
  props: {
    certificateAuthority: { default: null },
    disabled: { default: false },
    eventBus: { default: null },
    privateKeyType: { default: '' },
    useUserCertificates: { default: false },
    signMethod: { default: '' },
    shouldValidateKeyType: { type: Boolean, default: false }
  },
  data() {
    return {
      readerType: '',
      keyIsReaded: false,
      renderedReaderTypes: [],
      cloudProvider: CloudProvider.CloudKey
    };
  },
  computed: {
    componentDisabled() {
      return this.disabled || this.keyIsReaded;
    },
    CloudKeyReaderType() {
      return CloudKeyReaderType;
    },
    isFileSystemReaderEnabled() {
      return appSettingsHelper.isCloudKeyEnabled;
    },
    isFileSystemReaderRendered() {
      return this.renderedReaderTypes.includes(CloudKeyReaderType.FileSystem);
    },
    isFileSystemReaderVisible() {
      return this.readerType === CloudKeyReaderType.FileSystem;
    },
    isQrCodeReaderEnabled() {
      return appSettingsHelper.iitCloudProviderSettings.some(p => p.id === CloudProvider.CloudKey && p.enabled);
    },
    isQrCodeReaderRendered() {
      return this.renderedReaderTypes.includes(CloudKeyReaderType.QrCode);
    },
    isQrCodeReaderVisible() {
      return this.readerType === CloudKeyReaderType.QrCode;
    },
    signingParamsRequestMsg() {
      return `SigningParams-Request-${this.privateKeyType}-${this.signMethod}-${this.cloudProvider}`;
    },
    signingParamsResponseMsg() {
      return `SigningParams-Response-${this.privateKeyType}-${this.signMethod}-${this.cloudProvider}`;
    },
    signingParamsRequestChildMsg() {
      return `${this.signingParamsRequestMsg}-${this.readerType}`;
    },
    signingParamsResponseChildMsg() {
      return `${this.signingParamsResponseMsg}-${this.readerType}`;
    },
    isReaderTypeSelectorVisible() {
      return this.isFileSystemReaderEnabled && this.isQrCodeReaderEnabled;
    },
    isSeal() {
      return this.privateKeyType === PrivateKeyType.Seal;
    }
  },
  created() {
    this.subscribe();
    this.prepareComponent();
  },
  beforeDestroy() {
    this.unsubscribe();
  },
  methods: {
    appendAuthTypeToRendered(v) {
      if (!this.renderedReaderTypes.includes(v)) {
        this.renderedReaderTypes.push(v);
      }
    },
    onAuthTypeChanged(v) {
      this.appendAuthTypeToRendered(v);
      this.saveSelectedAuthTypeToStore(v);
      this.emitSignMethodChanged();
    },
    onKeyReaded(e) {
      this.keyIsReaded = true;
      this.$emit('keyReaded', e);
    },
    onReady(e) {
      this.$emit('ready', e);
    },
    onNotReady(e) {
      this.$emit('not-ready', e);
      this.keyIsReaded = false;
    },
    prepareComponent() {
      let readerType;

      let prov = this.getCloudProviderDataFromStore();
      if (prov) {
        readerType = prov.readerType;
      }

      if (readerType &&
          ((readerType === CloudKeyReaderType.FileSystem && !this.isFileSystemReaderEnabled) ||
           (readerType === CloudKeyReaderType.QrCode && !this.isQrCodeReaderEnabled))) {
        readerType = '';
      }

      if (!readerType) {
        readerType = this.isFileSystemReaderEnabled
          ? CloudKeyReaderType.FileSystem
          : this.isQrCodeReaderEnabled ? CloudKeyReaderType.QrCode : '';
      }

      this.readerType = readerType || '';

      if (this.readerType) {
        this.appendAuthTypeToRendered(this.readerType);
        this.saveSelectedAuthTypeToStore(this.readerType);
      }
    },
    reset() {
      this.keyIsReaded = false;
    },
    subscribe() {
      this.eventBus.$on('app-sign-reset-private-key', this.reset);
      this.eventBus.$on(this.signingParamsRequestMsg, this.onSigningParamsRequest);
      this.eventBus.$on('app-sign-method-changed', this.onSignMethodChanged);
    },
    unsubscribe() {
      this.eventBus.$off('app-sign-reset-private-key', this.reset);
      this.eventBus.$off(this.signingParamsRequestMsg, this.onSigningParamsRequest);
      this.eventBus.$off('app-sign-method-changed', this.onSignMethodChanged);
    },
    async onSigningParamsRequest(e) {
      let prms = await this.getSigningParams();
      this.eventBus.$emit(this.signingParamsResponseMsg, prms);
    },
    async getSigningParams() {
      return new Promise((resolve) => {
        const fn = (prms) => {
          this.eventBus.$off(this.signingParamsResponseChildMsg);
          resolve(prms);
        };
        this.eventBus.$on(this.signingParamsResponseChildMsg, fn);
        this.eventBus.$emit(this.signingParamsRequestChildMsg);
      });
    },
    onSignMethodChanged(prms) {
      if (prms.signMethod === SignMethod.Cloud && prms.signProvider === CloudProvider.CloudKey && !('keyReaderType' in prms)) {
        this.emitSignMethodChanged();
      }
    },
    emitSignMethodChanged() {
      this.eventBus.$emit('app-sign-method-changed', {
        signMethod: SignMethod.Cloud,
        signProvider: CloudProvider.CloudKey,
        keyReaderType: this.readerType
      });
    },
    getCloudProviderDataFromStore() {
      return this.isSeal
        ? this.$store.getters[_act.SIGN_GET_CLOUD_PROVIDER_SEAL](this.cloudProvider)
        : this.$store.getters[_act.SIGN_GET_CLOUD_PROVIDER](this.cloudProvider);
    },
    saveSelectedAuthTypeToStore(readerType) {
      let act = this.isSeal ? _act.SIGN_MUT_CLOUD_PROVIDER_SEAL : _act.SIGN_MUT_CLOUD_PROVIDER;
      this.$store.commit(act, {
        selectedCloud: this.cloudProvider,
        cloudProvider: { readerType }
      });
    }
  }
};
</script>
